import { useCallback, useEffect, useState } from 'react';

import {
  searchOrders, getOrderDetail, correctOrderEmail, resendTicket,
  refundOrder, cancelTicket,
  type SupportOrder, type SupportOrderDetail, type SupportTicket,
} from '../../lib/admin';
import { ApiError } from '../../lib/apiClient';
import { formatDateTime, formatMoney, shortId } from '../../lib/format';
import './AdminPages.css';

/**
 * Order & ticket support.
 *
 * Admin sees full buyer PII here, which is the inverse of every organizer- and
 * sponsor-facing screen. That is the platform's actual privacy model — Voleto is
 * the data controller — so this screen shows the data plainly rather than
 * pretending otherwise, and every action demands a reason that lands in the
 * audit log.
 */

const STATUS_COPY: Record<string, string> = {
  pending: 'Pending',
  complete: 'Paid',
  refunded: 'Refunded',
  partially_refunded: 'Partly refunded',
  expired: 'Expired',
};

/** Every mutation is behind this: a reason is mandatory, so the UI asks for one. */
function ReasonPrompt({
  title, description, confirmLabel, danger, busy, onCancel, onConfirm, extra,
}: {
  title: string;
  description: React.ReactNode;
  confirmLabel: string;
  danger?: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (reason: string) => void;
  extra?: React.ReactNode;
}) {
  const [reason, setReason] = useState('');
  return (
    <div className="sup-prompt">
      <h4 className="sup-prompt__title">{title}</h4>
      <div className="sup-prompt__body">{description}</div>
      {extra}
      <label className="field">
        <span className="field__label">Reason (recorded in the audit log) *</span>
        <input
          className="field__input" type="text" value={reason} autoFocus
          placeholder="e.g. buyer confirmed the typo by phone"
          onChange={(e) => setReason(e.target.value)}
        />
      </label>
      <div className="sup-prompt__actions">
        <button type="button" className="button button--ghost" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          type="button"
          className={`button ${danger ? 'button--secondary sup-danger' : 'button--primary'}`}
          disabled={busy || reason.trim().length < 3}
          onClick={() => onConfirm(reason.trim())}
        >
          {busy ? 'Working…' : confirmLabel}
        </button>
      </div>
    </div>
  );
}

function TicketRow({
  ticket, onVoid,
}: {
  ticket: SupportTicket;
  onVoid: (t: SupportTicket) => void;
}) {
  const voidable = ticket.status === 'active' || ticket.status === 'scanned';
  return (
    <tr className={ticket.status === 'cancelled' ? 'adm-row--suspended' : undefined}>
      <td className="mono" title={ticket.ticketUid}>{shortId(ticket.ticketUid)}</td>
      <td>{ticket.tierName ?? ' | '}</td>
      <td className="mono">{formatMoney(ticket.priceCents)}</td>
      <td><span className={`adm-pill adm-pill--${ticket.status}`}>{ticket.status}</span></td>
      <td>
        {ticket.scannedIn
          ? <span className="adm-pill adm-pill--approved">Scanned in</span>
          : <span className="adm-muted">Not used</span>}
      </td>
      <td>
        {ticket.cancellationReason
          ? <span className="adm-muted" title={ticket.cancellationReason}>
              {ticket.cancellationReason.slice(0, 40)}
            </span>
          : voidable
            ? <button type="button" className="button button--ghost adm-btn-sm"
                onClick={() => onVoid(ticket)}>Void</button>
            : <span className="adm-muted"> | </span>}
      </td>
    </tr>
  );
}

function OrderPanel({ orderId, onChanged }: { orderId: string; onChanged: () => void }) {
  const [order, setOrder] = useState<SupportOrderDetail | null>(null);
  const [action, setAction] = useState<null | 'email' | 'resend' | 'refund' | { void: SupportTicket }>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState('');
  const [refundAmount, setRefundAmount] = useState('');

  const load = useCallback(async () => {
    try {
      const d = await getOrderDetail(orderId);
      setOrder(d);
      setNewEmail(d.buyer.email);
      setError(null);
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'Could not load that order.');
    }
  }, [orderId]);

  useEffect(() => { void load(); }, [load]);

  const say = (msg: string) => { setFlash(msg); window.setTimeout(() => setFlash(null), 6000); };

  async function run(fn: () => Promise<string>) {
    setBusy(true); setError(null);
    try {
      say(await fn());
      setAction(null);
      await load();
      onChanged();
    } catch (err: unknown) {
      setError(err instanceof ApiError ? err.message : 'That action failed.');
    } finally { setBusy(false); }
  }

  if (!order) {
    return <div className="adm-card__body">{error ?? 'Loading…'}</div>;
  }

  const refundable = order.status === 'complete' || order.status === 'partially_refunded';
  const remaining = order.totalCents - order.refundedCents;

  return (
    <div className="adm-card__body sup-detail">
      {error && <p className="adm-error" role="alert">{error}</p>}
      {flash && <p className="adm-flash" role="status">{flash}</p>}

      <dl className="sup-facts">
        <div><dt>Buyer</dt><dd>{order.buyer.name || '-'}</dd></div>
        <div><dt>Email</dt><dd className="mono">{order.buyer.email}</dd></div>
        <div><dt>Phone</dt><dd className="mono">{order.buyer.phone ?? ' | '}</dd></div>
        <div><dt>Account</dt><dd>{order.buyer.isGuest ? 'Guest checkout' : 'Registered'}</dd></div>
        <div><dt>Placed</dt><dd className="mono">{formatDateTime(order.createdAt)}</dd></div>
        <div><dt>Paid</dt><dd className="mono">{formatMoney(order.totalCents)}</dd></div>
        {order.refundedCents > 0 && (
          <>
            <div><dt>Refunded</dt><dd className="mono sup-neg">−{formatMoney(order.refundedCents)}</dd></div>
            <div><dt>Net</dt><dd className="mono">{formatMoney(order.netCents)}</dd></div>
          </>
        )}
        <div><dt>Charge</dt><dd className="mono">{order.stripeChargeId ? shortId(order.stripeChargeId) : ' | '}</dd></div>
      </dl>

      {order.refundReason && (
        <p className="adm-muted adm-hint">Refund reason: {order.refundReason}</p>
      )}

      <table className="adm-table adm-table--dense">
        <thead>
          <tr>
            <th scope="col">Ticket</th><th scope="col">Tier</th><th scope="col">Price</th>
            <th scope="col">Status</th><th scope="col">Door</th><th scope="col">Action</th>
          </tr>
        </thead>
        <tbody>
          {order.tickets.map((t) => (
            <TicketRow key={t.ticketId} ticket={t} onVoid={(ticket) => setAction({ void: ticket })} />
          ))}
        </tbody>
      </table>

      {/* ------------------------------------------------------- action bar */}
      {!action && (
        <div className="sup-actions">
          <button type="button" className="button button--secondary adm-btn-sm"
            onClick={() => setAction('email')}>Correct email</button>
          <button type="button" className="button button--secondary adm-btn-sm"
            disabled={order.status !== 'complete'}
            title={order.status !== 'complete' ? 'Only a completed order has tickets to send' : undefined}
            onClick={() => setAction('resend')}>Resend ticket</button>
          <button type="button" className="button button--ghost adm-btn-sm sup-danger"
            disabled={!refundable}
            title={refundable ? undefined : 'Nothing left to refund'}
            onClick={() => setAction('refund')}>Refund</button>
        </div>
      )}

      {action === 'email' && (
        <ReasonPrompt
          title="Correct the buyer's email"
          description={
            <>
              Changes the contact address on <strong>this order only</strong>, the
              buyer&apos;s account is not touched. This does <strong>not</strong> resend
              the ticket; do that separately afterwards.
            </>
          }
          extra={
            <label className="field">
              <span className="field__label">New email</span>
              <input className="field__input mono" type="email" value={newEmail}
                onChange={(e) => setNewEmail(e.target.value)} />
            </label>
          }
          confirmLabel="Save email"
          busy={busy}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => run(async () => {
            const r = await correctOrderEmail(order.orderId, newEmail.trim(), reason);
            return `Email changed from ${r.previousEmail} to ${r.email}.`;
          })}
        />
      )}

      {action === 'resend' && (
        <ReasonPrompt
          title="Resend the ticket"
          description={
            <>
              Re-sends the order confirmation, with QR codes, to{' '}
              <strong className="mono">{order.buyer.email}</strong>. Check the address is
              right before sending.
            </>
          }
          confirmLabel="Resend now"
          busy={busy}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => run(async () => {
            const r = await resendTicket(order.orderId, reason);
            return `Confirmation queued to ${r.sentTo}.`;
          })}
        />
      )}

      {action === 'refund' && (
        <ReasonPrompt
          title="Refund this order"
          danger
          description={
            <>
              <p>
                Refunds through Stripe against charge{' '}
                <span className="mono">{order.stripeChargeId ? shortId(order.stripeChargeId) : ' | '}</span>.
                Up to <span className="mono">{formatMoney(remaining)}</span> remains refundable.
              </p>
              <ul className="sup-consequences">
                <li>
                  <strong>Full refund</strong> also voids this order&apos;s tickets and
                  returns the seats to inventory.
                </li>
                <li>
                  <strong>Partial refund</strong> leaves tickets valid, void specific
                  ones yourself if needed.
                </li>
                <li>
                  The refunded amount is subtracted from the organizer&apos;s gross at
                  settlement.
                </li>
              </ul>
            </>
          }
          extra={
            <label className="field">
              <span className="field__label">Amount (USD), leave blank for the full remaining amount</span>
              <input className="field__input mono" type="number" min="0" step="0.01"
                max={remaining / 100} value={refundAmount}
                placeholder={(remaining / 100).toFixed(2)}
                onChange={(e) => setRefundAmount(e.target.value)} />
            </label>
          }
          confirmLabel={refundAmount.trim()
            ? `Refund ${formatMoney(Math.round(Number(refundAmount) * 100))}`
            : `Refund ${formatMoney(remaining)} (full)`}
          busy={busy}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => run(async () => {
            const cents = refundAmount.trim() ? Math.round(Number(refundAmount) * 100) : undefined;
            const r = await refundOrder(order.orderId, reason, cents);
            setRefundAmount('');
            return `Refunded ${formatMoney(r.refundedCents)} (${r.stripeRefundId}).`
              + (r.ticketsVoided > 0 ? ` ${r.ticketsVoided} ticket(s) voided.` : '');
          })}
        />
      )}

      {action && typeof action === 'object' && 'void' in action && (
        <ReasonPrompt
          title={`Void ticket ${shortId(action.void.ticketUid)}`}
          danger
          description={
            <>
              <ul className="sup-consequences">
                <li>The QR code stops working, the door will refuse it.</li>
                <li>The seat returns to its tier and can be resold.</li>
                <li><strong>No money moves.</strong> Voiding is not refunding.</li>
                {action.void.scannedIn && (
                  <li className="sup-neg">
                    This ticket has already been scanned in, the holder may be inside
                    the venue right now.
                  </li>
                )}
              </ul>
            </>
          }
          confirmLabel="Void ticket"
          busy={busy}
          onCancel={() => setAction(null)}
          onConfirm={(reason) => run(async () => {
            const r = await cancelTicket(action.void.ticketId, reason);
            return `Ticket voided. ${r.note}`;
          })}
        />
      )}
    </div>
  );
}

export function AdminOrders() {
  const [orders, setOrders] = useState<SupportOrder[] | null>(null);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [status, setStatus] = useState('');
  const [openId, setOpenId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    searchOrders({ search: query || undefined, status: status || undefined })
      .then(setOrders)
      .catch((err: unknown) => {
        setError(err instanceof ApiError ? err.message : 'Could not search orders.');
        setOrders([]);
      })
      .finally(() => setLoading(false));
  }, [query, status]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="adm-page">
      <header className="adm-header">
        <div>
          <h1 className="adm-title">Orders &amp; tickets</h1>
          <p className="adm-lede">
            Support tooling: find a buyer&apos;s order, fix a mistyped address, resend a
            ticket, refund, or void. Every action is recorded against your admin account.
          </p>
        </div>
      </header>

      <form className="adm-filters" onSubmit={(e) => { e.preventDefault(); setQuery(search.trim()); }}>
        <input
          className="field__input adm-search" type="search"
          placeholder="Buyer email, name, or order id…"
          value={search} onChange={(e) => setSearch(e.target.value)}
        />
        <select className="adm-select" value={status} onChange={(e) => setStatus(e.target.value)}>
          <option value="">Any status</option>
          {['complete', 'pending', 'partially_refunded', 'refunded', 'expired'].map((s) => (
            <option key={s} value={s}>{STATUS_COPY[s]}</option>
          ))}
        </select>
        <button type="submit" className="button button--secondary">Search</button>
        {(query || status) && (
          <button type="button" className="button button--ghost"
            onClick={() => { setSearch(''); setQuery(''); setStatus(''); }}>Clear</button>
        )}
      </form>

      {error && <p className="adm-status adm-error" role="alert">{error}</p>}
      {loading && <p className="adm-status">Searching…</p>}
      {!loading && orders?.length === 0 && (
        <p className="adm-status adm-muted">No orders match.</p>
      )}

      <div className="adm-list">
        {orders?.map((o) => (
          <article key={o.orderId} className="adm-card">
            <header className="adm-card__head">
              <div className="adm-card__title-wrap">
                <button type="button" className="adm-disclosure"
                  aria-expanded={openId === o.orderId}
                  onClick={() => setOpenId(openId === o.orderId ? null : o.orderId)}>
                  <span className="adm-disclosure__caret" aria-hidden="true">
                    {openId === o.orderId ? '▾' : '▸'}
                  </span>
                  <span className="adm-card__title">{o.buyer.name || o.buyer.email}</span>
                </button>
                <p className="adm-card__meta">
                  <span className="mono">{o.buyer.email}</span>
                  {' · '}{o.event.title}
                  {' · '}<span className="mono" title={o.orderId}>{shortId(o.orderId)}</span>
                  {' · '}<span className="mono">{formatDateTime(o.createdAt)}</span>
                </p>
              </div>
              <div className="adm-card__status">
                <span className={`adm-pill adm-pill--${o.status}`}>{STATUS_COPY[o.status] ?? o.status}</span>
                <span className="sup-total mono">
                  {formatMoney(o.netCents)}
                  {o.refundedCents > 0 && (
                    <span className="adm-muted"> of {formatMoney(o.totalCents)}</span>
                  )}
                </span>
              </div>
            </header>

            {openId === o.orderId && <OrderPanel orderId={o.orderId} onChanged={load} />}
          </article>
        ))}
      </div>
    </div>
  );
}
